import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardCheck, LayoutDashboard, LineChart, ShieldAlert, AlertCircle, Lock } from 'lucide-react';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');

.login {
  --ink: #14232e; --ink-2: #4a5b68; --line: #dde3e8; --brand: #0e5a6b; --brand-d: #0a4552; --risk: #b93815; --risk-bg: #fdebe5;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  min-height: 100vh; display: grid; grid-template-columns: minmax(360px, 5fr) 6fr; color: var(--ink); background: #f4f6f7;
}
.login *, .login *::before, .login *::after { box-sizing: border-box; }

/* Left: product context */
.login-side { position: relative; display: flex; flex-direction: column; justify-content: space-between; background: #0b3f4b; color: #fff; padding: 56px 56px 44px; overflow: hidden; }
.login-side-inner { margin: auto 0; max-width: 460px; }
.login-side h2 { margin: 0 0 12px; font-size: 30px; line-height: 1.2; font-weight: 700; letter-spacing: -0.5px; }
.login-side-inner > p { margin: 0 0 36px; font-size: 15px; line-height: 1.6; color: #b9d3da; }
.login-side ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 18px; }
.login-side li { display: flex; gap: 14px; align-items: flex-start; }
.login-side li > span { width: 36px; height: 36px; border-radius: 9px; display: grid; place-items: center; background: rgba(255,255,255,.1); flex-shrink: 0; }
.login-side li b { display: block; font-size: 14px; font-weight: 600; }
.login-side li small { display: block; margin-top: 2px; font-size: 13px; line-height: 1.45; color: #a9c5cd; }
.login-side-foot { margin-top: 36px; font-size: 12px; color: #8fb2bc; }
.hazard { position: absolute; left: 0; right: 0; bottom: 0; height: 10px; background: repeating-linear-gradient(-45deg, #f2b705 0 14px, #14232e 14px 28px); }

.login-side { background-size: cover; background-position: center; }
.login-overlay { position: absolute; inset: 0; background: rgba(11,63,75,.88); }
.login-side > *:not(.login-overlay):not(.hazard) { position: relative; }
.login-side-logo { height: 45px; width: auto; align-self: flex-start; }

/* Right: sign in */
.login-main { display: grid; place-items: center; padding: 32px; }
.login-card { width: 100%; max-width: 400px; background: #fff; border: 1px solid var(--line); border-radius: 14px; padding: 36px 32px; box-shadow: 0 8px 28px rgba(20,35,46,.08); }
.login-card h1 { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.3px; }
.login-sub { margin: 8px 0 24px; font-size: 14px; line-height: 1.55; color: var(--ink-2); }

.login-error { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 16px; padding: 12px 14px; border-radius: 10px; background: var(--risk-bg); border: 1px solid #f5c6b8; color: #8a2a0f; font-size: 13px; line-height: 1.45; }
.login-error svg { flex-shrink: 0; margin-top: 1px; }

.ms-btn { display: flex; align-items: center; justify-content: center; gap: 12px; width: 100%; height: 46px; border-radius: 8px; border: 1px solid #8c9aa5; background: #fff; color: var(--ink); font-size: 14px; font-weight: 600; text-decoration: none; cursor: pointer; transition: background .15s, border-color .15s; }
.ms-btn:hover { background: #f4f7f8; border-color: var(--brand); }
.ms-btn:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
.ms-btn--busy { opacity: .7; }

.login-note { display: flex; align-items: center; justify-content: center; gap: 6px; margin: 20px 0 0; font-size: 12px; color: var(--ink-2); text-align: center; }

@media (max-width: 860px) {
  .login { grid-template-columns: 1fr; }
  .login-side { padding: 32px 24px 28px; }
  .login-side h2 { font-size: 22px; }
  .login-side ul, .login-side-foot { display: none; }
  .login-side-inner { margin: 24px 0 0; }
  .login-side-inner > p { margin-bottom: 0; }
  .login-main { padding: 24px 16px; }
}
`;

// Set to the promo background image path used in your old AuthPage.css, e.g. '/login-bg.jpg'
const PROMO_IMAGE = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80&auto=format&fit=crop';
const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN || (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : '')).replace(/\/$/, '');
const MICROSOFT_AUTH_URL = `${API_ORIGIN}/api/auth/microsoft`;

const ERRORS = {
  not_authorized: 'Your Microsoft account is not enabled for this system. Ask a superadmin to add you.',
  sso_unavailable: 'Microsoft sign-in is not set up yet. Contact the system administrator.',
  sso_denied: 'Microsoft sign-in was canceled or denied. Try again.',
  sso_state: 'Sign-in verification failed. Enable cookies for localhost and start sign-in again.',
  sso_exchange: 'Microsoft could not complete sign-in. Check the backend log and Entra redirect URI configuration.',
  sso_tenant: 'This Microsoft account belongs to a different organization than the configured tenant.',
  sso_email: 'Microsoft did not return an email address for this account.',
  sso_failed: 'We could not complete Microsoft sign-in. Try again.',
};

const FEATURES = [
  { icon: ClipboardCheck, title: 'Observer form links', text: 'Generated automatically for every business unit.' },
  { icon: LayoutDashboard, title: 'Live observation register', text: 'Filter, search and close actions as they come in.' },
  { icon: LineChart, title: 'Business unit analytics', text: 'See trends by department, shift and location.' },
  { icon: ShieldAlert, title: 'Safe vs. at-risk tracking', text: 'Spot patterns before they become incidents.' },
];

const MicrosoftMark = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');

  return (
    <div className="login">
      <style>{STYLES}</style>
      <aside className="login-side" style={PROMO_IMAGE ? { backgroundImage: `url(${PROMO_IMAGE})` } : undefined}>
        <div className="login-overlay" aria-hidden="true" />
        <img className="login-side-logo" src="/logo.png" alt="UFL HSE" />
        <div className="login-side-inner">
          <h2>Behavior-Based Safety &amp; VFL Observation System</h2>
          <p>Capture, track and act on safety observations across every business unit.</p>
          <ul>
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <li key={title}>
                <span><Icon size={18} /></span>
                <div><b>{title}</b><small>{text}</small></div>
              </li>
            ))}
          </ul>
        </div>
        <div className="login-side-foot">FMCG Manufacturing · Snacks, Dairies, Swan</div>
        <div className="hazard" aria-hidden="true" />
      </aside>

      <main className="login-main">
        <div className="login-card">
          <h1>Sign in</h1>
          <p className="login-sub">Use your authorized Microsoft work account to open the admin console.</p>

          {errorCode && (
            <div className="login-error" role="alert">
              <AlertCircle size={17} />
              <span>{ERRORS[errorCode] || ERRORS.sso_failed}</span>
            </div>
          )}

          <a className="ms-btn" href={MICROSOFT_AUTH_URL}>
            <MicrosoftMark />
            Sign in with Microsoft
          </a>

          <p className="login-note"><Lock size={13} /> Access is limited to accounts approved by a superadmin.</p>
        </div>
      </main>
    </div>
  );
}